import { useCallback, useEffect, useRef, useState } from 'react';
import messageService from '../services/messageService';
import trimVideo from '../utils/trimVideo';
import { UPLOAD_CONCURRENCY, clipSec } from '../components/messages/albumModel';

/**
 * The outbox: messages that have left the composer but not yet the building.
 *
 * A send used to hold the draft hostage — the text sat in the input, greyed
 * out, until the server answered. On a slow connection that reads as a broken
 * send button, and it blocks you from writing the next line. So the bubble now
 * appears the instant you hit send, marked SENDING, and the composer is
 * immediately empty and ready for the next one.
 *
 * Pending messages live here rather than being spliced into the thread's
 * message array, because that array is replaced wholesale every time the
 * thread reloads (poll, reconnect, refresh). Kept separate, they survive a
 * reload; merged only at render, they still sort and group like real ones.
 */

let seq = 0;
/** Temp ids are prefixed so nothing mistakes one for a server id. */
export const TEMP_PREFIX = 'outbox:';
export const isTempId = (id) => typeof id === 'string' && id.startsWith(TEMP_PREFIX);
const nextTempId = () => `${TEMP_PREFIX}${Date.now()}:${seq++}`;

/**
 * Build the bubble to show while the real one is in flight. It has to be
 * message-shaped enough for MessageBubble to render it — same kind, same
 * fields — since the whole point is that nothing visibly changes when the
 * server's version arrives.
 */
export function optimisticMessage(draft, { tempId, viewerUserId, now = Date.now() }) {
  const base = {
    id: tempId,
    threadId: draft.threadId,
    senderId: viewerUserId,
    createdAt: new Date(now).toISOString(),
    kind: draft.kind,
    reactions: [],
    // Sealed from the first frame: the sender's own copy never shows the content either.
    ...(draft.viewOnce || draft.payload?.viewOnce ? { viewOnce: { openedBy: [], wiped: false, viewLimit: draft.viewLimit || draft.payload?.viewLimit || 1 } } : {}),
  };
  switch (draft.kind) {
    case 'image':
      return { ...base, imageUrl: draft.previewUrl, imageFilename: draft.filename, imageSizeBytes: draft.sizeBytes };
    case 'audio':
      return { ...base, audioUrl: draft.previewUrl, audioDurationSec: draft.durationSec, audioPeaks: draft.peaks || null };
    case 'video':
      // Shown as its poster frame while it goes up; the clip plays once the server says ready.
      return { ...base, videoStatus: 'uploading', videoThumbnailUrl: draft.previewUrl, videoDurationSec: draft.durationSec, videoTrimStartMs: draft.trim?.startMs, videoTrimEndMs: draft.trim?.endMs, videoSizeBytes: draft.sizeBytes };
    case 'gif':
      return { ...base, gifUrl: draft.payload?.gifUrl, gifLabel: draft.payload?.gifLabel, gifWidth: draft.payload?.gifWidth, gifHeight: draft.payload?.gifHeight };
    case 'link':
      // The preview is deliberately not carried over: the server re-scrapes and
      // is the authority on it, and a preview that changes shape on delivery
      // reads as the message being edited under you.
      return { ...base, body: draft.payload?.body, linkUrl: draft.payload?.linkUrl, quote: draft.payload?.quote || null };
    case 'album':
      // The grid appears at once from the local previews; each tile's upload state rides
      // the outbox entry (`_outbox.items`), not the message.
      return {
        ...base,
        albumCount: draft.items.length,
        albumItems: draft.items.map((i) => ({
          id: i.clientId, kind: i.kind, url: i.previewUrl, thumbnailUrl: i.previewUrl, sizeBytes: i.size, filename: i.name,
          durationSec: i.kind === 'video' ? clipSec(i) : null, trimStartMs: i.trim?.startMs, trimEndMs: i.trim?.endMs, status: 'ready',
        })),
      };
    default:
      // A reply shows its quote from the first frame; the server's copy replaces it.
      return { ...base, body: draft.payload?.body, quote: draft.payload?.quote || null };
  }
}

/**
 * Merge pending entries into the delivered list for rendering.
 *
 * Pending always sorts last: it was written most recently by definition, and
 * anchoring it to the bottom means the bubble doesn't hop up the thread when
 * the server's timestamp comes back a second or two off from ours.
 */
export function mergeOutbox(messages, entries) {
  if (!entries.length) return messages;
  return [...messages, ...entries.map((e) => ({
    ...e.message,
    // A clip whose upload failed says so on its frame, not SENDING · 0% forever.
    ...(e.message.kind === 'video' && e.state === 'failed' ? { videoStatus: 'failed' } : {}),
    _outbox: { state: e.state, error: e.error, progress: e.progress, ...(e.items ? { items: e.items } : {}) },
  }))];
}

/** A fresh per-item state list for an album draft: everything queued, nothing uploaded. */
export const albumItemStates = (draft) => draft.items.map((i) => ({ clientId: i.clientId, state: i.serverId ? 'ready' : 'queued', progress: i.serverId ? 100 : 0, error: '' }));

/**
 * A clip is cut on the device first (a remux, no re-encode — see trimVideo) so a two-minute
 * file trimmed to twenty seconds uploads twenty seconds. The cut rebases the trim to its own
 * clock; a file that won't cut goes up whole with the trim numbers, and the player loops
 * inside them either way. The cut is cached on the draft so a retry doesn't do it twice.
 */
/** The device cut remuxes in memory; past this a phone can't, so the file goes up whole with its window. */
export const MAX_CUT_BYTES = 200 * 1024 * 1024;

async function sendVideo(d, onProgress) {
  let file = d.file;
  let trim = d.trim || null;
  if (trim && trim.endMs > trim.startMs) {
    if (d.cut === undefined && d.file.size > MAX_CUT_BYTES) d.cut = null;
    if (d.cut === undefined) {
      try { d.cut = await trimVideo(d.file, trim); } catch { d.cut = null; }
    }
    if (d.cut) { file = d.cut.file; trim = { startMs: 0, endMs: d.cut.clipMs }; }
  }
  return messageService.sendVideo(d.threadId, file, { trim, viewOnce: !!d.viewOnce, viewLimit: d.viewLimit || 1, onProgress });
}

/** One album item up: a photo as is, a clip cut on the device first (see sendVideo). */
async function stageAlbumItem(threadId, i, onProgress) {
  if (i.kind === 'video') {
    let file = i.file;
    let trim = i.trim || null;
    if (trim && trim.endMs > trim.startMs) {
      if (i.cut === undefined && i.file.size > MAX_CUT_BYTES) i.cut = null;
      if (i.cut === undefined) {
        try { i.cut = await trimVideo(i.file, trim); } catch { i.cut = null; }
      }
      if (i.cut) { file = i.cut.file; trim = { startMs: 0, endMs: i.cut.clipMs }; }
    }
    return messageService.stageAlbumVideo(threadId, file, { trim, onProgress });
  }
  return messageService.stageAlbumImage(threadId, i.file, { onProgress });
}

/**
 * An album: every item that hasn't landed yet goes up, three at a time in album order, each
 * reporting its own state through `onItem(clientId, changes)`. Then — only once nothing is
 * still failed — the album is sent as the ordered list of what landed. A failed item leaves
 * the album waiting: RETRY sends it again, SEND WITHOUT IT drops it (useOutbox owns both).
 * Returns the normal `{ success, data }` shape, or `{ success: false, partial: true }` when
 * items failed and the album is being held.
 */
async function sendAlbum(d, onItem, { only = null } = {}) {
  const todo = d.items.filter((i) => !i.serverId && (!only || only.includes(i.clientId)));
  let next = 0;
  let failed = 0;
  const worker = async () => {
    while (next < todo.length) {
      const i = todo[next++];
      onItem(i.clientId, { state: 'uploading', progress: 0, error: '' });
      const r = await stageAlbumItem(d.threadId, i, (pct) => onItem(i.clientId, { progress: pct }));
      if (r.success && r.data?.id) {
        i.serverId = r.data.id;
        onItem(i.clientId, { state: r.data.status === 'encoding' ? 'processing' : 'ready', progress: 100 });
      } else {
        failed += 1;
        onItem(i.clientId, { state: 'failed', progress: 0, error: r.error || 'Upload failed', isNetwork: !!r.isNetwork });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, todo.length) }, worker));
  if (failed || d.items.some((i) => !i.serverId)) return { success: false, partial: true };
  return messageService.sendAlbum(d.threadId, { itemIds: d.items.map((i) => i.serverId), viewOnce: !!d.viewOnce, viewLimit: d.viewLimit || 1 });
}

function send(entry, onProgress, onItem, opts) {
  const d = entry.draft;
  if (d.kind === 'image') return messageService.sendImage(d.threadId, d.file, { viewOnce: !!d.viewOnce, viewLimit: d.viewLimit || 1 });
  if (d.kind === 'video') return sendVideo(d, onProgress);
  if (d.kind === 'audio') return messageService.sendAudio(d.threadId, d.blob, d.durationSec, d.peaks || null);
  if (d.kind === 'album') return sendAlbum(d, onItem, opts);
  return messageService.sendMessage(d.threadId, d.payload);
}

export function useOutbox({ viewerUserId, onDelivered }) {
  const [entries, setEntries] = useState([]);
  // The dispatcher reads entries through a ref: a retry fired from an event
  // listener would otherwise close over whatever the list was when the listener
  // was attached.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const deliveredRef = useRef(onDelivered);
  deliveredRef.current = onDelivered;

  const patch = useCallback((tempId, changes) => {
    setEntries((prev) => prev.map((e) => (e.id === tempId ? { ...e, ...changes } : e)));
  }, []);

  const patchItem = useCallback((tempId, clientId, changes) => {
    setEntries((prev) => prev.map((e) => (e.id === tempId && e.items
      ? { ...e, items: e.items.map((i) => (i.clientId === clientId ? { ...i, ...changes } : i)) }
      : e)));
  }, []);

  const remove = useCallback((tempId) => {
    setEntries((prev) => {
      const gone = prev.find((e) => e.id === tempId);
      // An object URL for a local preview is only alive as long as the pending
      // bubble that shows it; the delivered message carries a real URL.
      if (gone?.draft?.previewUrl) URL.revokeObjectURL(gone.draft.previewUrl);
      gone?.draft?.items?.forEach((i) => { if (i.previewUrl) URL.revokeObjectURL(i.previewUrl); });
      return prev.filter((e) => e.id !== tempId);
    });
  }, []);

  const dispatchRef = useRef(null);
  const dispatch = useCallback(async (entry, opts = {}) => {
    patch(entry.id, { state: 'sending', error: '', progress: 0 });
    const r = await send(entry, (pct) => patch(entry.id, { progress: pct }), (clientId, changes) => patchItem(entry.id, clientId, changes), opts);
    if (r.success) {
      deliveredRef.current?.(r.data);
      remove(entry.id);
      return;
    }
    // An album with items that didn't land waits for RETRY / SEND WITHOUT IT; the tiles
    // say which. It is 'failed' so the auto-retry below leaves it alone.
    if (r.partial) {
      // A tile re-queued while the pool was running missed it: run again for those.
      const now = entriesRef.current.find((e) => e.id === entry.id);
      const requeued = (now?.items || []).filter((i) => i.state === 'queued').map((i) => i.clientId);
      if (requeued.length) { dispatchRef.current?.({ ...now, draft: entry.draft }, { only: requeued }); return; }
      patch(entry.id, { state: 'failed', error: '' });
      return;
    }
    // No response at all means it never reached the server. Hold it and retry
    // on reconnect rather than telling someone their message failed when the
    // wifi just blinked.
    patch(entry.id, {
      state: r.isNetwork ? 'queued' : 'failed',
      error: r.isNetwork ? '' : (r.error || 'Send failed'),
    });
  }, [patch, patchItem, remove]);
  dispatchRef.current = dispatch;

  const enqueue = useCallback((draft) => {
    const tempId = nextTempId();
    const entry = {
      id: tempId,
      threadId: draft.threadId,
      draft,
      state: 'sending',
      error: '',
      message: optimisticMessage(draft, { tempId, viewerUserId }),
      ...(draft.kind === 'album' ? { items: albumItemStates(draft) } : {}),
    };
    setEntries((prev) => [...prev, entry]);
    dispatch(entry);
    return tempId;
  }, [dispatch, viewerUserId]);

  const retry = useCallback((tempId) => {
    const entry = entriesRef.current.find((e) => e.id === tempId);
    if (!entry || entry.state === 'sending') return;
    if (entry.items) {
      // Every failed item goes back in the queue; what landed stays landed.
      const failed = entry.items.filter((i) => i.state === 'failed').map((i) => i.clientId);
      failed.forEach((cid) => patchItem(tempId, cid, { state: 'queued', progress: 0, error: '' }));
    }
    dispatch(entry);
  }, [dispatch, patchItem]);

  /** An album tile: send just that item again. */
  const retryItem = useCallback((tempId, clientId) => {
    const entry = entriesRef.current.find((e) => e.id === tempId);
    if (!entry?.items) return;
    const item = entry.items.find((i) => i.clientId === clientId);
    if (!item || item.state !== 'failed') return;
    // Another item may still be uploading: let the one worker pool handle it on the way.
    patchItem(tempId, clientId, { state: 'queued', progress: 0, error: '' });
    if (entry.state !== 'sending') dispatch(entry, { only: [clientId] });
  }, [dispatch, patchItem]);

  /** SEND WITHOUT IT: drop the failed items and send the album with what landed. */
  const dropFailed = useCallback((tempId) => {
    const entry = entriesRef.current.find((e) => e.id === tempId);
    if (!entry?.items) return;
    const gone = new Set(entry.items.filter((i) => i.state === 'failed').map((i) => i.clientId));
    if (!gone.size) return;
    const keep = entry.draft.items.filter((i) => !gone.has(i.clientId));
    if (!keep.length) { remove(tempId); return; }
    entry.draft.items.filter((i) => gone.has(i.clientId)).forEach((i) => { if (i.previewUrl) URL.revokeObjectURL(i.previewUrl); });
    entry.draft.items = keep;
    const next = {
      ...entry,
      items: entry.items.filter((i) => !gone.has(i.clientId)),
      message: { ...entry.message, albumCount: keep.length, albumItems: entry.message.albumItems.filter((a) => !gone.has(a.id)) },
    };
    setEntries((prev) => prev.map((e) => (e.id === tempId ? next : e)));
    dispatch(next);
  }, [dispatch, remove]);

  const discard = useCallback((tempId) => remove(tempId), [remove]);

  // Auto-retry anything held offline: on the 'online' event, and on a poll as a
  // backstop for when that event doesn't fire (it is unreliable on mobile
  // Safari, which is exactly where this matters).
  useEffect(() => {
    const hasQueued = entries.some((e) => e.state === 'queued');
    if (!hasQueued) return undefined;
    const run = () => {
      if (navigator.onLine === false) return;
      entriesRef.current.filter((e) => e.state === 'queued').forEach(dispatch);
    };
    window.addEventListener('online', run);
    const iv = setInterval(run, 6000);
    return () => { window.removeEventListener('online', run); clearInterval(iv); };
  }, [entries, dispatch]);

  const forThread = useCallback(
    (threadId) => entries.filter((e) => e.threadId === threadId),
    [entries],
  );

  return { entries, enqueue, retry, retryItem, dropFailed, discard, forThread };
}
